import React, { useContext, useState, useRef, useEffect } from 'react';
import {
  Box, Typography, useTheme, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, IconButton, CircularProgress, Alert
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Header from '../../../../components/Header';
import { UserContext } from '../../../../context/UserContext';
import { tokens } from '../../../../theme';
import axios from 'axios';

import { Html5Qrcode } from 'html5-qrcode';

function CouponVerification() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user, token } = useContext(UserContext);

  const [qrOpen, setQrOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrError, setQrError] = useState('');
  const [verifyError, setVerifyError] = useState('');

  const [infoOpen, setInfoOpen] = useState(false);
  const [verifiedData, setVerifiedData] = useState(null);

  const ValidationSchema = Yup.object().shape({
    couponCode: Yup.string().trim().required('Kupon kodu zorunludur'),
  });

  const verifyCoupon = async (couponCode) => {
    setLoading(true);
    setVerifyError('');
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}api/alcago/brand/verifyCouponCode`, null, {
        params: { code: couponCode, token },
      });

      const ok = res?.data?.success;
      const c = res?.data?.coupon;

      if (!ok || !c) {
        setVerifyError('Kupon bulunamadı veya kullanılamaz durumda.');
        setVerifiedData(null);
        setInfoOpen(true);
        return;
      }

      const ownerName = [c.user_name, c.user_surname].filter(Boolean).join(' ') || (user?.name ?? '—');
      const price = c.discounted_price_tl ?? c.product_price ?? 0;

      setVerifiedData({
        walletId: c.id,
        couponCode,
        productName: c.product_name || '—',
        ownerName,
        price,
        currency: 'TRY',
      });
      setInfoOpen(true);
    } catch (err) {
      if (err.response?.status !== 404) {
        setVerifyError('Doğrulama sırasında bir hata oluştu.');
      } else {
        setVerifyError('Kupon bulunamadı veya kullanılamaz durumda.');
      }
      setVerifiedData(null);
      setInfoOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUseCoupon = async (walletId) => {
    setLoading(true);
    try {
      const res = await axios.patch(`${process.env.REACT_APP_API_URL}api/alcago/brand/useCoupon`, null, {
        params: { walletId, token },
      });
      const ok = res?.data?.success;
      if (ok) {
        setInfoOpen(false);
      } else {
        setVerifyError('Kupon kullanılamadı (zaten kullanılmış olabilir).');
      }
    } catch (err) {
      setVerifyError('Kupon kullanma sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, currency = 'TRY', locale = 'tr-TR') =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value ?? 0);

  return (
    <Box mt="20px">
      <Box ml="20px">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Header title="Kupon Doğrulama" subtitle="Kupon kodu girin veya QR kod okutun" />
        </Box>
      </Box>

      <Box mt={3} mx="20px">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <Box
            flex={1}
            p={3}
            border={`1px solid ${colors.grey[400]}`}
            borderRadius="8px"
            bgcolor={colors.primary[400]}
            minWidth={280}
          >
            <Typography variant="h6" mb={2}>Kupon Kodu</Typography>
            <Formik
              initialValues={{ couponCode: '' }}
              validationSchema={ValidationSchema}
              onSubmit={async (values) => {
                await verifyCoupon(values.couponCode, 'form');
              }}
            >
              {({ errors, touched, handleChange, handleBlur, values, handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    name="couponCode"
                    label="Kupon kodunu girin"
                    value={values.couponCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.couponCode && Boolean(errors.couponCode)}
                    helperText={touched.couponCode && errors.couponCode}
                  />

                  <Stack direction="row" spacing={1} mt={2} alignItems="center">
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<CheckCircleOutlineIcon />}
                      disabled={loading}
                    >
                      Doğrula
                    </Button>

                    <Button
                      type="button"
                      variant="outlined"
                      startIcon={<QrCodeScannerIcon />}
                      onClick={() => {
                        setQrError('');
                        setVerifyError('');
                        setQrOpen(true);
                      }}
                      disabled={loading}
                    >
                      QR Kod Oku
                    </Button>

                    {loading && <CircularProgress size={24} />}
                  </Stack>
                </form>
              )}
            </Formik>
          </Box>

          <Box
            flex={1}
            p={3}
            border={`1px solid ${colors.grey[400]}`}
            borderRadius="8px"
            bgcolor={colors.primary[400]}
            minWidth={280}
          >
            <Typography variant="h6" mb={1}>Nasıl Çalışır?</Typography>
            <Typography variant="body2" color="text.secondary">
              Kupon kodunu girip <b>Doğrula</b>’ya tıklayın ya da <b>QR Kod Oku</b> ile kodu okutun.
              Doğrulama sonrası ürün bilgileri görünür. <b>Kullan</b> derseniz kupon tüketilir.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <QRScannerDialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        onDetected={async (text) => {
          setQrOpen(false);
          await verifyCoupon(String(text || '').trim(), 'qr');
        }}
        onError={(msg) => setQrError(msg)}
      />

      {(qrError || verifyError) && (
        <Box mx="20px" mt={2}>
          {qrError && (
            <Alert severity="warning" onClose={() => setQrError('')}>
              {qrError}
            </Alert>
          )}
          {verifyError && (
            <Alert severity="error" onClose={() => setVerifyError('')}>
              {verifyError}
            </Alert>
          )}
        </Box>
      )}

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Kupon Bilgileri
          <IconButton
            aria-label="kapat"
            onClick={() => setInfoOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {verifiedData ? (
            <Stack spacing={1.2}>
              <Row label="Kupon Kodu" value={verifiedData.couponCode} />
              <Row label="Ürün Adı" value={verifiedData.productName} />
              <Row label="Sahip Kullanıcı" value={verifiedData.ownerName} />
              <Row
                label="Ödenecek / Ödenen Fiyat"
                value={formatCurrency(verifiedData.price, verifiedData.currency || 'TRY')}
              />
            </Stack>
          ) : (
            <Typography color={verifyError ? 'error' : 'text.primary'}>
              {verifyError || 'Bilgi bulunamadı.'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Kapat</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!verifiedData?.walletId) return;
              await handleUseCoupon(verifiedData.walletId);
            }}
            disabled={loading || !verifiedData?.walletId}
          >
            Kullan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Row({ label, value }) {
  return (
    <Stack direction="row" spacing={1}>
      <Typography sx={{ minWidth: 180 }} color="text.secondary">
        {label}:
      </Typography>
      <Typography fontWeight={500}>{value}</Typography>
    </Stack>
  );
}

function QRScannerDialog({ open, onClose, onDetected, onError }) {
  const videoContainerId = 'qr-reader-container';
  const html5QrRef = useRef(null);
  const [permissionGranted, setPermissionGranted] = useState(null); // null=bekleniyor, true/false

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop()); // sadece izin için
      setPermissionGranted(true);
    } catch (err) {
      setPermissionGranted(false);
      onError?.('Kamera izni verilmedi. QR okutma çalışmayacak.');
    }
  };

  useEffect(() => {
    if (open) {
      requestCameraPermission();
    } else {
      setPermissionGranted(null);
    }
  }, [open]);

  useEffect(() => {
    let html5Qr;

    const startScanner = async () => {
      if (!permissionGranted) return;

      try {
        const el = document.getElementById(videoContainerId);
        if (el) el.innerHTML = '';

        html5Qr = new Html5Qrcode(videoContainerId);
        html5QrRef.current = html5Qr;

        const config = { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 };

        await html5Qr.start(
          { facingMode: { exact: 'environment' } },
          config,
          (decodedText) => {
            stopScanner().finally(() => onDetected?.(decodedText));
          },
          () => { }
        ).catch(async () => {
          await html5Qr.start(
            { facingMode: 'environment' },
            config,
            (decodedText) => {
              stopScanner().finally(() => onDetected?.(decodedText));
            }
          );
        });
      } catch (err) {
        onError?.('Kamera başlatılamadı. İzin veya cihaz erişimini kontrol edin.');
        try {
          if (html5QrRef.current) {
            await html5QrRef.current.start(
              { facingMode: 'user' },
              { fps: 10, qrbox: { width: 250, height: 250 } },
              (decodedText) => {
                stopScanner().finally(() => onDetected?.(decodedText));
              }
            );
          }
        } catch {

        }
      }
    };

    const stopScanner = async () => {
      try {
        const inst = html5QrRef.current;
        if (inst && inst.isScanning) {
          await inst.stop();
        }
        if (inst) {
          await inst.clear();
        }
      } catch {

      }
    };

    if (open && permissionGranted) {
      startScanner();
    }
    return () => { stopScanner(); };
  }, [open, permissionGranted, onDetected, onError]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        QR Kod Okuma
        <IconButton
          aria-label="kapat"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {permissionGranted === null && (
          <Typography>Kamera izni kontrol ediliyor...</Typography>
        )}
        {permissionGranted === false && (
          <Typography color="error">
            Kamera izni verilmedi. Tarayıcı ayarlarından izin verin.
          </Typography>
        )}
        {permissionGranted === true && (
          <Box
            id={videoContainerId}
            sx={{
              width: '100%',
              height: 320,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          />
        )}
        <Typography variant="caption" color="text.secondary">
          Kod otomatik algılandığında dialog kapanır.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Vazgeç</Button>
      </DialogActions>
    </Dialog>
  );
}

export default CouponVerification;
